import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faArrowUpFromBracket } from '@fortawesome/pro-regular-svg-icons/faArrowUpFromBracket';
import { faDatabase } from '@fortawesome/pro-regular-svg-icons/faDatabase';
import { faDiamond } from '@fortawesome/pro-regular-svg-icons/faDiamond';
import { faDiamondHalfStroke } from '@fortawesome/pro-regular-svg-icons/faDiamondHalfStroke';
import { faFileMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons/faFileMagnifyingGlass';
import { faFolderClosed } from '@fortawesome/pro-regular-svg-icons/faFolderClosed';
import { faLevelUp } from '@fortawesome/pro-regular-svg-icons/faLevelUp';
import { faList } from '@fortawesome/pro-regular-svg-icons/faList';
import { faMerge } from '@fortawesome/pro-regular-svg-icons/faMerge';
import { faPercent } from '@fortawesome/pro-regular-svg-icons/faPercent';
import { faPercentage } from '@fortawesome/pro-regular-svg-icons/faPercentage';
import { faRulerTriangle } from '@fortawesome/pro-regular-svg-icons/faRulerTriangle';
import { faShareNodes } from '@fortawesome/pro-regular-svg-icons/faShareNodes';
import { faStairs } from '@fortawesome/pro-regular-svg-icons/faStairs';
import { faTable } from '@fortawesome/pro-regular-svg-icons/faTable';
import { faTableList } from '@fortawesome/pro-regular-svg-icons/faTableList';
import { faUsers } from '@fortawesome/pro-regular-svg-icons/faUsers';
import { TreeItem, TreeItemIndex } from 'react-complex-tree';

import { ENTRY_OBJECT_TYPES } from 'quotient/DirectoryOrganizer/types';
import { colors } from 'quotient/theme/foundations/colors/colors';
import { BlueprintType } from 'quotient/types';

import { NavigatorDataObject, NavigatorWorksheet, PlanVizNodeTypes, WORKBOOK_TYPES, WORKSHEET_TYPES } from './types';

type FormattedTreeData = Record<TreeItemIndex, TreeItem<NavigatorDataObject>>;

const componentColors: string[] = ['secondary.600', 'tertiary.600', 'quaternary.600', 'primary.600'];

export const componentColorMap = new Map();

type DataObjectWithComponentInstance = {
  component_instance_id?: string;
  component_instance?: { [key: string]: string };
};

export const getComponentColorForWorksheetV2 = (item: NavigatorWorksheet): string => {
  /**
   * Only the following items should get a color when groups is enabled:
   * 1. Component instance directory if multi-worksheets
   * 2. Component instance worksheet itself if there's only 1 worksheet.
   * 3. Blueprint instance worksheet (currently blueprints are singleton worksheets)
   *
   */

  const isMainComponentInstance =
    item.object_type === ENTRY_OBJECT_TYPES.COMPONENT_INSTANCE ||
    (!!item.component_instance_id && item.root_directory_id === item.directory_id);
  if (isMainComponentInstance) {
    const componentInstanceId =
      item.object_type === ENTRY_OBJECT_TYPES.COMPONENT_INSTANCE ? item.object_id : item.component_instance_id;
    if (!componentColorMap.has(componentInstanceId)) {
      const colorIndex = componentColorMap.size % componentColors.length;
      componentColorMap.set(componentInstanceId, componentColors[colorIndex]);
      return componentColors[colorIndex];
    }
    if (componentColorMap.has(componentInstanceId)) {
      return componentColorMap.get(componentInstanceId);
    }
  }

  if (item.blueprint_instance_id && item.blueprint_instance_type !== BlueprintType.CREDITING) {
    if (!componentColorMap.has(item.blueprint_instance_id)) {
      const colorIndex = componentColorMap.size % componentColors.length;
      componentColorMap.set(item.blueprint_instance_id, componentColors[colorIndex]);
      return componentColors[colorIndex];
    }
    if (componentColorMap.has(item.blueprint_instance_id)) {
      return componentColorMap.get(item.blueprint_instance_id);
    }
  }

  return 'none';
};

export const getComponentColorForWorksheet = (worksheet: NavigatorWorksheet): string => {
  if (!worksheet.component_instance_id && !worksheet.blueprint_instance_id) {
    return 'none';
  }

  if (worksheet.blueprint_instance_id && worksheet.blueprint_instance_type !== BlueprintType.CREDITING) {
    if (!componentColorMap.has(worksheet.blueprint_instance_id)) {
      const colorIndex = componentColorMap.size % componentColors.length;
      componentColorMap.set(worksheet.blueprint_instance_id, componentColors[colorIndex]);
      return componentColors[colorIndex];
    }
    if (componentColorMap.has(worksheet.blueprint_instance_id)) {
      return componentColorMap.get(worksheet.blueprint_instance_id);
    }
  } else if (worksheet.component_instance_id) {
    if (!componentColorMap.has(worksheet.component_instance_id)) {
      const colorIndex = componentColorMap.size % componentColors.length;
      componentColorMap.set(worksheet.component_instance_id, componentColors[colorIndex]);
      return componentColors[colorIndex];
    }
    if (componentColorMap.has(worksheet.component_instance_id)) {
      return componentColorMap.get(worksheet.component_instance_id);
    }
  }

  return 'none';
};

export const cacheColors = (navigatorObjects: DataObjectWithComponentInstance[]) => {
  /** We will pre-populate the componentColorMap based on a given a set of data objects */
  navigatorObjects.forEach((obj, index) => {
    if (!obj.component_instance_id && !obj.component_instance) {
      return;
    }

    const componentInstanceId = obj.component_instance_id || obj.component_instance?.id;
    if (!componentInstanceId) {
      return;
    }

    if (!componentColorMap.has(componentInstanceId)) {
      const color = componentColors[index % componentColors.length];
      componentColorMap.set(componentInstanceId, color);
    }
  });

  return componentColorMap;
};

export const getCachedComponentColorMapInHexCode = () => {
  return Array.from(componentColorMap.entries()).reduce((acc, [key, val]) => {
    const colorKeys = val.split('.');
    const hexColor = (colors[colorKeys[0] as keyof typeof colors] as any)[colorKeys[1]];
    return {
      ...acc,
      [key]: hexColor,
    };
  }, {});
};

export const getTreeDataV2 = (entries: NavigatorWorksheet[]) => {
  const formattedTreeData: FormattedTreeData = {};

  const entryIds = new Set(entries.map((entry) => entry.id));

  entries.forEach((entry: NavigatorWorksheet) => {
    const componentGroupColor = getComponentColorForWorksheetV2(entry);
    formattedTreeData[entry.id] = {
      index: entry.id,
      isFolder: [ENTRY_OBJECT_TYPES.DIRECTORY, ENTRY_OBJECT_TYPES.COMPONENT_INSTANCE].includes(
        entry.object_type as ENTRY_OBJECT_TYPES,
      ),
      // we have to do this instead of taking straight from children_order
      // so that correct worksheets gets filtered out when we search by a string.
      children: (entry as any).children_order?.filter((childId: string) => entryIds.has(childId)) || [],
      data: {
        ...entry,
        componentGroupColor,
      },
    };
  });

  return formattedTreeData;
};

export const getTreeData = (worksheets: NavigatorWorksheet[]): FormattedTreeData => {
  const formattedTreeData: FormattedTreeData = {
    root: {
      index: 'root',
      isFolder: true,
      children: [],
      data: {
        object: 'root',
        id: 'root',
        name: 'root',
        description: 'root',
        type: 'root',
        is_hidden: false,
      },
    },
  };

  worksheets.forEach((worksheet: NavigatorWorksheet) => {
    const componentGroupColor = getComponentColorForWorksheet(worksheet);
    formattedTreeData[worksheet.id] = {
      index: worksheet.id,
      isFolder: false,
      children: [],
      data: {
        ...worksheet,
        componentGroupColor,
      },
    };
    formattedTreeData.root.children?.push(worksheet.id);
  });

  return formattedTreeData;
};

export const worksheetIconTypes: Record<WORKSHEET_TYPES | ENTRY_OBJECT_TYPES | BlueprintType, IconDefinition> = {
  component_instance: faDiamond,
  derived: faMerge,
  api: faTable,
  raw: faDatabase,
  payout_summary: faTable,
  payout_settings: faTable,
  period_group: faTable,
  hierarchy: faUsers,
  historical_detailed: faFileMagnifyingGlass,
  historical_payouts: faTable,
  output: faTable,
  quota: faTable,
  data_flow: faTable,
  unified_employee_assumption: faTable,
  master: faTable,
  attribute: faTable,
  report_model: faTable,
  blueprint_instance: faRulerTriangle,
  object: faTable,
  [BlueprintType.CREDITING]: faList,
  [BlueprintType.FLAT_RATE]: faPercentage,
  [BlueprintType.TIERED_RATE]: faStairs,
  [BlueprintType.ROW_BASED_TIERED_RATE]: faStairs,
  [BlueprintType.CONDITIONAL]: faShareNodes,
  [BlueprintType.TRUE_UP]: faLevelUp,
  workspace_model: faTable,
  [ENTRY_OBJECT_TYPES.DIRECTORY]: faFolderClosed,
  [ENTRY_OBJECT_TYPES.WORKSHEET]: faTable,
};

export const planVizIconsByType: Record<string, IconDefinition> = {
  [PlanVizNodeTypes.OUTPUT]: faArrowUpFromBracket,
  [PlanVizNodeTypes.CREDITS]: faList,
  [PlanVizNodeTypes.BLUEPRINT]: faRulerTriangle,
  [BlueprintType.FLAT_RATE]: faPercent,
  [BlueprintType.TRUE_UP]: faRulerTriangle,
  [BlueprintType.TIERED_RATE]: faStairs,
  [BlueprintType.ROW_BASED_TIERED_RATE]: faStairs,
  [BlueprintType.CONDITIONAL]: faShareNodes,
};

export const getWorksheetTypeIcon = (
  worksheet: NavigatorWorksheet,
  nodeType?: PlanVizNodeTypes,
  workbookType?: WORKBOOK_TYPES,
  isChild?: boolean,
) => {
  if (worksheet.icon) {
    return worksheet.icon;
  }

  if (worksheet.object_type && worksheet.object_type !== ENTRY_OBJECT_TYPES.WORKSHEET) {
    if (worksheet.blueprint_instance_type) {
      return (
        worksheetIconTypes[worksheet.blueprint_instance_type as BlueprintType] ??
        worksheetIconTypes[worksheet.object_type]
      );
    }
    return worksheetIconTypes[worksheet.object_type];
  }

  if (!worksheet.type) {
    return faTableList;
  }

  // handles icons on Component Workbooks
  if (workbookType && workbookType === WORKBOOK_TYPES.COMPONENT) {
    return worksheet.type === WORKSHEET_TYPES.API ? faDiamondHalfStroke : faDiamond;
  }

  const isWorksheetLike = !nodeType || [PlanVizNodeTypes.WORKSHEET, PlanVizNodeTypes.INPUT].includes(nodeType);

  if (isWorksheetLike) {
    if (worksheet.blueprint_instance_id && worksheet.blueprint_instance_type) {
      return (
        worksheetIconTypes[worksheet.blueprint_instance_type as BlueprintType] ??
        worksheetIconTypes[WORKSHEET_TYPES.BLUEPRINT_INSTANCE as WORKSHEET_TYPES]
      );
    }
    return worksheet.component_instance_id && !isChild
      ? worksheetIconTypes[WORKSHEET_TYPES.COMPONENT_INSTANCE]
      : worksheetIconTypes[worksheet.type] ?? faTableList;
  }

  return planVizIconsByType[nodeType];
};

export const getParentIdsToExpand = (
  activeNodeId: string,
  data: Record<TreeItemIndex, TreeItem>,
  rootId: string,
  // eslint-disable-next-line @typescript-eslint/no-inferrable-types
  isFiltered: boolean = false,
): TreeItemIndex[] => {
  /**
   * If isFiltered is false, then we will only open up the group that houses the active Node.
   * If isFiltered is true, that means that we are searching for specific items that live
   * inside the navigator. If there are children that matched agains the searched term and they
   * live in a group, then those groups must be opened up to show the children. Therefore,
   * we will return those parent ids in this function.
   */
  if (!isFiltered) {
    const parentItem = Object.values(data).find(
      (item) => item.children?.includes(activeNodeId) && item.index !== rootId,
    );
    return parentItem ? [parentItem.index] : [];
  }

  // if we are filtering the data in the navigator, then we need to open up the
  // folders to show the children that match the search term.
  return Object.values(data)
    .filter((item) => item.isFolder)
    .map((item) => item.index);
};
